'use client';

import { Fragment, useState } from 'react';
import { groupGiftTiers } from '@eliadmin/core/src/utils/gift-tier-groups';
import { ChevronDown as FiChevronDown } from 'lucide-react';
import type { ProductGift } from '@eliadmin/core/src/api/products/types/product-types';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { giftThresholdLabel } from '@eliadmin/core/src/i18n/gift-labels';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import { GiftRewardContents } from '@eliadmin/core/src/components/product/detail/gift-reward';

const copy = {
  sk: {
    more: 'Pozrieť všetky darčeky',
    less: 'Skryť možnosti',
    unknown: 'Podľa podmienok ponuky',
    or: 'alebo',
  },
  cs: {
    more: 'Zobrazit všechny dárky',
    less: 'Skrýt možnosti',
    unknown: 'Podle podmínek nabídky',
    or: 'nebo',
  },
  en: {
    more: 'See all gifts',
    less: 'Hide options',
    unknown: 'Offer conditions apply',
    or: 'or',
  },
};

/** Shared preview of alternative rewards; expanding it never selects a tier. */
export function GiftTierOptions({
  gift,
  currentTierId,
}: {
  gift: ProductGift;
  currentTierId?: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const settings = useGlobalStore(s => s.generalLayout.currency_settings);
  const language = locale.toLowerCase().replaceAll('_', '-').split('-')[0];
  const labels =
    copy[language === 'sk' ? 'sk' : language === 'cs' || language === 'cz' ? 'cs' : 'en'];

  if (!gift.tiers.length) return null;

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="mt-2"
      data-gift-options={gift.id}
    >
      <CollapsibleTrigger
        type="button"
        className="flex min-h-9 cursor-pointer items-center gap-1 text-xs font-medium text-primary"
      >
        {expanded ? labels.less : labels.more}
        <FiChevronDown
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none',
            expanded && 'rotate-180',
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent
        anim="none"
        inert={!expanded}
        className="ml-0 motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down"
      >
        <div className="pb-1 pt-1">
          <ul className="m-0 list-none space-y-1 p-0">
            {groupGiftTiers(gift.tiers).map(group => (
              <li key={group[0].id} data-gift-threshold-group={group[0].id}>
                <span className="block px-2 text-xs font-semibold">
                  {group[0].order_price === null
                    ? labels.unknown
                    : `${giftThresholdLabel(locale)} ${formatPrice(group[0].order_price, currency, settings)}`}
                </span>
                <ul className="m-0 list-none p-0">
                  {group.map((tier, index) => (
                    <Fragment key={tier.id}>
                      {index > 0 && (
                        <li className="my-1 flex items-center gap-3 px-2 text-xs text-muted-foreground" data-gift-alternative-separator>
                          <span className="h-px flex-1 bg-border" aria-hidden="true" />
                          <span>{labels.or}</span>
                          <span className="h-px flex-1 bg-border" aria-hidden="true" />
                        </li>
                      )}
                      <li
                        data-gift-tier-id={tier.id}
                        aria-current={tier.id === currentTierId ? 'true' : undefined}
                        className={cn(
                          'flex flex-col gap-1.5 rounded-lg border px-2 py-1.5',
                          tier.id === currentTierId ? 'border-primary/25 bg-primary/10' : 'border-transparent',
                        )}
                      >
                        <GiftRewardContents reward={tier} />
                      </li>
                    </Fragment>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
